import { useEffect, useRef } from "react";

const GOLD = [197, 168, 128];
const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Animated fine-line sound-wave ribbon (canvas).
 * Colour and rhythm shift gently with scroll progress for a living feel.
 */
export default function WaveField({ color = "18,17,16", lines = 46, className = "" }) {
  const canvasRef = useRef(null);
  const raf = useRef(0);
  const mouse = useRef({ x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 });
  const scroll = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const base = color.split(",").map(Number);
    let w = 0;
    let h = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      mouse.current.tx = e.clientX / window.innerWidth;
      mouse.current.ty = e.clientY / window.innerHeight;
    };
    window.addEventListener("pointermove", onMove);

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      scroll.current = max > 0 ? Math.min(1, window.scrollY / max) : 0;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    let sp = 0; // eased scroll progress
    let phase = 0; // continuous phase accumulator -> smooth tempo
    let last = performance.now();
    const draw = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      ctx.clearRect(0, 0, w, h);

      mouse.current.x += (mouse.current.tx - mouse.current.x) * 0.05;
      mouse.current.y += (mouse.current.ty - mouse.current.y) * 0.05;
      const mx = mouse.current.x - 0.5;
      const my = mouse.current.y - 0.5;

      sp += (scroll.current - sp) * 0.04;
      // colour drifts toward gold with scroll
      const cr = Math.round(lerp(base[0], GOLD[0], sp * 0.7));
      const cg = Math.round(lerp(base[1], GOLD[1], sp * 0.7));
      const cb = Math.round(lerp(base[2], GOLD[2], sp * 0.7));

      // Steady, smooth tempo — speed eases with scroll but phase stays continuous.
      const speed = 0.34 * (1 + sp * 0.22);
      phase += dt * speed;
      const ph = phase;

      const baseY = h * 0.5;
      const segments = Math.max(60, Math.floor(w / 8));
      const spread = h * 0.34 * (1 + sp * 0.12);
      const step = w / segments;

      for (let i = 0; i < lines; i++) {
        const s = lines > 1 ? i / (lines - 1) : 0.5;
        const c = s - 0.5;

        ctx.beginPath();
        for (let j = 0; j <= segments; j++) {
          const x = j * step;
          const nx = x / w;

          const env =
            Math.sin(nx * Math.PI * 2.1 + ph * 0.9) *
            Math.cos(nx * Math.PI * 1.3 - ph * 0.6 + my * 1.2);

          const glide =
            Math.sin(nx * Math.PI * 1.6 + ph * 1.1) * (h * 0.06) +
            Math.sin(nx * Math.PI * 3.4 - ph * 1.6) * (h * 0.025);

          const twist = Math.sin(nx * Math.PI * 4 + ph * 1.25 + c * 3.2 + mx * 2);

          const y =
            baseY + glide + c * spread * env + twist * (h * 0.02) * (0.4 + Math.abs(c));

          if (j === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }

        const edge = Math.abs(c) * 2;
        const alpha = 0.05 + edge * 0.16 + sp * 0.05;
        ctx.strokeStyle = `rgba(${cr},${cg},${cb},${alpha})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }

      raf.current = requestAnimationFrame(draw);
    };
    raf.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("scroll", onScroll);
    };
  }, [color, lines]);

  return <canvas ref={canvasRef} className={className} style={{ width: "100%", height: "100%" }} />;
}
